import * as echarts from 'echarts';
import React, { useEffect, useRef, useState } from 'react';
import 'echarts-gl';
import {
  ChartIcons,
  MoonIcons,
  SunIcons,
  TargetIcons,
  TrendingUpIcons,
  UsersIcons,
} from '../../../../../icons/icons';
import { Modal } from '../../../../../ui/modal/modal';
import { Applicant, TabData } from '../../../../../utils/types/JobOpenings';
import styles from '../../ApplicantAnalysis/Modal.module.css';
import { ScatterModal } from '../../ModalContent/ScatterModal';

type Bar3DProps = {
  scatter: boolean;
  svgActiveTab: number;
  data: Applicant[];
  selectedCandidates: TabData[];
  setSelectedCandidates: (tabDataObject: TabData[]) => void | undefined;
  setTabTitles: (titles: string[]) => void | undefined;
  setActiveTab: (value: number) => void;
  tabTitles: string[];
  colorList: string[];
  projection: string;
  boxWidthDepth: number[];
  maxLegion: number;
  useSkillPercentage: boolean;
  additionalSkills?: { skills: { skill: string; weight: number }[] };
  jobTitles?: { jobTitles: string[] };
  keywords?: { keywords: { keyword: string; weight: number }[] };
  behavioralAttributes?: { behavioralAttributes: string[] };
  // New props for email selection functionality
  selectedApplicantsForEmail?: Set<string>;
  onApplicantSelection?: (applicant: any) => void;
  isApplicantSelected?: (applicant: any) => boolean;
  currentTab?: string;
  onCandidateClick?: (
    candidate: Applicant,
    tileCandidates?: Applicant[],
    tileName?: string,
  ) => void;
};

export const Bar3D = ({
  scatter,
  svgActiveTab,
  data,
  setSelectedCandidates,
  selectedCandidates,
  setTabTitles,
  setActiveTab,
  tabTitles,
  colorList,
  projection,
  boxWidthDepth,
  maxLegion,
  useSkillPercentage,
  additionalSkills,
  jobTitles,
  keywords,
  behavioralAttributes,
  selectedApplicantsForEmail,
  onApplicantSelection,
  isApplicantSelected,
  currentTab,
  onCandidateClick,
}: Bar3DProps) => {
  const [segmentedCandidates, setSegmentedCandidates] = useState<Applicant[]>(
    [],
  );
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const chartRef = useRef<HTMLDivElement>(null);
  const newData = data.map((item) => ({
    ...item,
    symbol: 'circle',
  }));
  const [isDarkTheme, setIsDarkTheme] = useState(false);
  interface SelectedPoint {
    name: string;
    value: [number, number, number];
    symbol: string;
    normalizedValue: [number, number, number];
    [key: string]: any;
  }
  const [selectedPoint, setSelectedPoint] =
    useState<SelectedPoint | null>(null);
  const [hoveredPoint, setHoveredPoint] = useState(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number | null>(null);
  const rotationRef = useRef({ x: 0.01, y: 0.01 });
  const mouseRef = useRef({ x: 0, y: 0, isDown: false });
  const stats = data
    ? {
      totalCandidates: data.length,
      avgSkillScore: Math.round(
        data.reduce((sum, item) => sum + item.value[0], 0) / data.length,
      ),
      avgExperience:
          Math.round(
            (data.reduce((sum, item) => sum + item.value[1], 0) / data.length) *
              10,
          ) / 10,
      avgBehaviorScore: Math.round(
        data.reduce((sum, item) => sum + item.value[2], 0) / data.length,
      ),
    }
    : {
      totalCandidates: 0,
      avgSkillScore: 0,
      avgExperience: 0,
      avgBehaviorScore: 0,
      topPerformer: null,
    };

  useEffect(() => {
    if (!data || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();

    // Set canvas size
    canvas.width = rect.width * window.devicePixelRatio;
    canvas.height = rect.height * window.devicePixelRatio;
    if (ctx) {
      ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
    }

    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const scaleX = rect.width / 4;
    const scaleY = rect.height / 4;
    const scaleZ = 100;

    let rotation = { x: 0, y: 0 };

    interface ProjectedPoint {
      x: number;
      y: number;
      z: number;
      scale: number;
    }

    const project3D = (
      x: number,
      y: number,
      z: number,
      rotX: number,
      rotY: number,
    ): ProjectedPoint => {
      // Apply rotations
      const cosX = Math.cos(rotX),
        sinX = Math.sin(rotX);
      const cosY = Math.cos(rotY),
        sinY = Math.sin(rotY);

      // Rotate around Y axis
      const x1 = x * cosY - z * sinY;
      const z1 = x * sinY + z * cosY;

      // Rotate around X axis
      const y1 = y * cosX - z1 * sinX;
      const z2 = y * sinX + z1 * cosX;

      // Project to 2D
      const perspective = 300;
      const scale = perspective / (perspective + z2);

      return {
        x: centerX + x1 * scale,
        y: centerY - y1 * scale,
        z: z2,
        scale: scale,
      };
    };

    interface NormalizedApplicant extends Applicant {
      normalizedValue: [number, number, number];
    }

    interface NormalizeDataFn {
      (data: Applicant[]): NormalizedApplicant[];
    }

    const normalizeData: NormalizeDataFn = (data) => {
      const maxSkill = Math.max(...data.map((d) => d.value[0]));
      const maxExp = Math.max(...data.map((d) => d.value[1]));
      const maxBehavior = Math.max(...data.map((d) => d.value[2]));

      return data.map((item) => ({
        ...item,
        normalizedValue: [
          (item.value[0] / maxSkill - 0.5) * 2,
          (item.value[1] / maxExp - 0.5) * 2,
          (item.value[2] / maxBehavior - 0.5) * 2,
        ],
      }));
    };

    const normalizedData = normalizeData(data);

    const animate = () => {
      if (!ctx) return;
      ctx.clearRect(0, 0, rect.width, rect.height);

      // Auto-rotate
      rotation.x += rotationRef.current.x;
      rotation.y += rotationRef.current.y;

      // Project and sort points by depth
      const projectedPoints = normalizedData
        .map((point) => {
          const projected = project3D(
            point.normalizedValue[0] * scaleX,
            point.normalizedValue[1] * scaleY,
            point.normalizedValue[2] * scaleZ,
            rotation.x,
            rotation.y,
          );
          return {
            ...point,
            projected,
            distance: Math.sqrt(
              Math.pow(mouseRef.current.x - projected.x, 2) +
                Math.pow(mouseRef.current.y - projected.y, 2),
            ),
          };
        })
        .sort((a, b) => {
          // First sort by Z depth for proper rendering
          const zDiff = b.projected.z - a.projected.z;
          if (Math.abs(zDiff) > 0.1) return zDiff;

          // If Z values are close, sort by distance to mouse for better hover detection
          return a.distance - b.distance;
        }); // Draw points
      projectedPoints.forEach((point, index) => {
        const { projected } = point;
        const radius = Math.max(8 * projected.scale, 4);

        // Create gradient for 3D sphere effect
        const gradient = ctx.createRadialGradient(
          projected.x - radius * 0.3,
          projected.y - radius * 0.3,
          0,
          projected.x,
          projected.y,
          radius,
        );

        gradient.addColorStop(0, isDarkTheme ? '#ffffff40' : '#ffffff80');
        gradient.addColorStop(0.7, isDarkTheme ? '#ffffff20' : '#00000020');
        gradient.addColorStop(1, isDarkTheme ? '#00000040' : '#00000020');

        // Draw sphere
        ctx.beginPath();
        ctx.arc(projected.x, projected.y, radius, 0, 2 * Math.PI);
        ctx.fillStyle = gradient;
        ctx.fill();

        // Add subtle border
        ctx.strokeStyle = isDarkTheme ? '#ffffff20' : '#00000020';
        ctx.lineWidth = 1;
        ctx.stroke();

        // Highlight hovered/selected point
        if (hoveredPoint === index) {
          // Inner glow
          const glowGradient = ctx.createRadialGradient(
            projected.x,
            projected.y,
            radius * 0.8,
            projected.x,
            projected.y,
            radius * 2,
          );

          if (isDarkTheme) {
            glowGradient.addColorStop(0, 'rgba(129, 140, 248, 0.4)');
            glowGradient.addColorStop(1, 'rgba(129, 140, 248, 0)');
          } else {
            glowGradient.addColorStop(0, 'rgba(99, 102, 241, 0.3)');
            glowGradient.addColorStop(1, 'rgba(99, 102, 241, 0)');
          }

          // Draw glow effect
          ctx.beginPath();
          ctx.arc(projected.x, projected.y, radius * 2, 0, 2 * Math.PI);
          ctx.fillStyle = glowGradient;
          ctx.fill();

          // Draw highlight ring
          ctx.beginPath();
          ctx.arc(projected.x, projected.y, radius + 2, 0, 2 * Math.PI);
          ctx.strokeStyle = isDarkTheme ? '#818cf8' : '#6366f1';
          ctx.lineWidth = 2;
          ctx.stroke();

          // Draw pulsing ring
          const pulseSize = radius + 4 + Math.sin(Date.now() / 200) * 2;
          ctx.beginPath();
          ctx.arc(projected.x, projected.y, pulseSize, 0, 2 * Math.PI);
          ctx.strokeStyle = isDarkTheme
            ? 'rgba(129, 140, 248, 0.3)'
            : 'rgba(99, 102, 241, 0.2)';
          ctx.lineWidth = 1;
          ctx.stroke();
        }
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    // Mouse interactions
    interface MouseEventWithClient extends MouseEvent {
      clientX: number;
      clientY: number;
    }

    const handleMouseMove = (e: MouseEventWithClient) => {
      const canvas = canvasRef.current as HTMLCanvasElement;
      const rect = canvas.getBoundingClientRect();
      const x: number = e.clientX - rect.left;
      const y: number = e.clientY - rect.top;

      if (mouseRef.current.isDown) {
        const deltaX: number = x - mouseRef.current.x;
        const deltaY: number = y - mouseRef.current.y;
        rotationRef.current.x = deltaY * 0.01;
        rotationRef.current.y = deltaX * 0.01;
      } else {
        // Check for point hover
        const projectedPoints = normalizedData.map((point) => {
          const projected = project3D(
            point.normalizedValue[0] * scaleX,
            point.normalizedValue[1] * scaleY,
            point.normalizedValue[2] * scaleZ,
            rotation.x,
            rotation.y,
          );
          return { ...point, projected };
        });

        let foundHover: number | null = null;
        // Get closest point within hover threshold
        let minDistance = Infinity;
        projectedPoints.forEach((point, index) => {
          // Skip points that are too far behind the camera
          if (point.projected.z < -200) return;

          const distance: number = Math.sqrt(
            (x - point.projected.x) ** 2 + (y - point.projected.y) ** 2,
          );

          // Use adaptive hover radius based on point's Z position
          const hoverRadius = Math.max(10, 20 * (1 - point.projected.z / 500));

          if (distance < hoverRadius && distance < minDistance) {
            minDistance = distance;
            foundHover = index;
          }
        });

        setHoveredPoint(foundHover);
        canvas.style.cursor = foundHover !== null ? 'pointer' : 'grab';
      }

      mouseRef.current.x = x;
      mouseRef.current.y = y;
    };

    const handleMouseDown = () => {
      mouseRef.current.isDown = true;
      canvas.style.cursor = 'grabbing';
      rotationRef.current = { x: 0, y: 0 };
    };

    const handleMouseUp = () => {
      mouseRef.current.isDown = false;
      canvas.style.cursor = 'grab';
      rotationRef.current = { x: 0.01, y: 0.01 };
    };

    interface ClickEvent extends MouseEvent {
      clientX: number;
      clientY: number;
    }

    interface SelectedPoint {
      name: string;
      value: [number, number, number];
      symbol: string;
      normalizedValue: [number, number, number];
      [key: string]: any;
    }

    const handleClick = (e: ClickEvent) => {
      if (hoveredPoint !== null) {
        const clickedApplicant = normalizedData[hoveredPoint];

        // Handle email selection for Unsuccessful Applicants tab
        if (
          currentTab === 'Unsuccessful Applicants' &&
          onApplicantSelection &&
          clickedApplicant
        ) {
          onApplicantSelection(clickedApplicant);
          return;
        }

        if (onCandidateClick && clickedApplicant) {
          onCandidateClick(clickedApplicant);
          return;
        }

        setSelectedPoint(
          normalizedData[hoveredPoint] as unknown as SelectedPoint,
        );
        setIsModalOpen(true);
      }
    };

    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('mousedown', handleMouseDown);
    canvas.addEventListener('mouseup', handleMouseUp);
    canvas.addEventListener('click', handleClick);

    animate();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('mousedown', handleMouseDown);
      canvas.removeEventListener('mouseup', handleMouseUp);
      canvas.removeEventListener('click', handleClick);
    };
  }, [data, isDarkTheme, hoveredPoint, colorList, maxLegion]);

  useEffect(() => {
    let resizeObserver: ResizeObserver | undefined;
    let resizeHandler: (() => void) | undefined;
    let scatterModalHandler: ((event: Event) => void) | undefined;

    if (chartRef.current) {
      // Dispose existing chart before reinitializing
      echarts.dispose(chartRef.current);
      const myChart = echarts.init(
        chartRef.current,
        isDarkTheme ? 'dark' : undefined,
      );
      // Add custom event listener for the tooltip button
      const openModalEvent = (event: CustomEvent) => {
        const { x, y, z } = event.detail;
        const filteredCandidates = data.filter((item) => {
          const itemValue = item.value as number[];

          // Get the skill value that should be used for comparison
          let itemSkillValue = itemValue[0];
          if (useSkillPercentage) {
            // If in percentage mode, get the skillPercentage from the original data
            itemSkillValue = Number(item.value[0] || 0);
          }

          const tolerance = 0.001;
          return (
            Math.abs(itemSkillValue - x) < tolerance &&
            Math.abs(itemValue[1] - y) < tolerance &&
            Math.abs(itemValue[2] - z) < tolerance
          );
        });

        if (filteredCandidates.length > 0) {
          setSegmentedCandidates(filteredCandidates);
          setIsModalOpen(true);
        }
      };

      // Add global handler for the tooltip button
      scatterModalHandler = (event: Event) =>
        openModalEvent(event as CustomEvent);
      window.addEventListener('openScatterModal', scatterModalHandler);

      // Add global helper to dispatch the event
      (window as any).openScatterModal = (x: number, y: number, z: number) => {
        const event = new CustomEvent('openScatterModal', {
          detail: { x, y, z },
        });
        window.dispatchEvent(event);
      };

      const option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'none',
          },
          show: true,
          enterable: true,
          confine: true,
          appendToBody: true,
          showDelay: 0,
          hideDelay: 300,
          transitionDuration: 0.1,
          alwaysShowContent: false,
          triggerOn: 'mousemove|click',
          position: function (
            pos: number[],
            params: any,
            dom: any,
            rect: any,
            size: any,
          ) {
            const [x, y] = pos;
            const chartWidth = size.viewSize[0];
            const chartHeight = size.viewSize[1];
            const tooltipWidth = dom.offsetWidth;
            const tooltipHeight = dom.offsetHeight;

            // Add some padding from the point
            const padding = 20;

            // Default position is to the right and below the point
            let left = x + padding;
            let top = y + padding;

            // If tooltip would go off the right side, show it to the left instead
            if (left + tooltipWidth > chartWidth) {
              left = x - tooltipWidth - padding;
            }

            // If tooltip would go off the bottom, show it above instead
            if (top + tooltipHeight > chartHeight) {
              top = y - tooltipHeight - padding;
            }

            // Make sure tooltip doesn't go off the left or top
            left = Math.max(padding, left);
            top = Math.max(padding, top);

            return { left, top };
          },
          backgroundColor: isDarkTheme
            ? 'rgba(26, 32, 44, 0.95)'
            : 'rgba(255, 255, 255, 0.98)',
          borderColor: isDarkTheme
            ? 'rgba(66, 153, 225, 0.2)'
            : 'rgba(0, 0, 0, 0.1)',
          borderWidth: 1,
          padding: 0,
          extraCssText:
            'box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15); border-radius: 8px;',
          formatter: function (params: any) {
            // Make sure we have a valid params object with values
            if (!params || !params.value) return '';

            // Find all candidates at this position, accounting for useSkillPercentage
            const candidates = data.filter((d) => {
              try {
                let dSkillValue = d.value[0];
                let dKeywordValue = d.value[3] !== undefined ? d.value[3] : 0;
                let paramsKeywordValue =
                  params.value[3] !== undefined ? params.value[3] : 0;

                if (useSkillPercentage) {
                  // When in percentage mode, use the skillPercentage from the original data
                  dSkillValue = Number(d.value[0] || 0);
                }

                // Use a small tolerance for floating point comparison
                const tolerance = 0.001;
                const matchesSkill =
                  Math.abs(dSkillValue - params.value[0]) < tolerance;
                const matchesExperience =
                  Math.abs(d.value[1] - params.value[1]) < tolerance;
                const matchesBehavior =
                  Math.abs(d.value[2] - params.value[2]) < tolerance;
                const matchesKeyword =
                  Math.abs(dKeywordValue - paramsKeywordValue) < tolerance;

                // Include keyword matching for completeness, especially important when keywords are 0
                return (
                  matchesSkill && matchesExperience && matchesBehavior
                  // matchesKeyword
                );
              } catch (error) {
                return false;
              }
            });

            if (candidates.length === 0) {
              // Force tooltip to display even if no candidates are found
              // This ensures tooltip shows for zero values when useSkillPercentage is true
              if (params.value) {
                const fallbackExperience = params.value[1] || 0;
                const fallbackSkill = params.value[0] || 0;
                const fallbackBehavior = params.value[2] || 0;
                const fallbackKeyword = params.value[3] || 0;

                return `
                  <div style="padding: 10px; font-family: sans-serif; background: ${
  isDarkTheme
    ? 'rgba(26, 32, 44, 0.95)'
    : 'rgba(255, 255, 255, 0.98)'
}; border-radius: 8px; min-width: 200px; color: ${
  isDarkTheme ? '#e2e8f0' : '#1e293b'
};">
                    <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; border-bottom: 1px solid ${
  isDarkTheme ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'
}; padding-bottom: 8px;">
                      <div style="font-weight: bold; font-size: 14px;">Candidate Data Point</div>
                      <div onclick="window.openScatterModal(${
  params.value[0]
}, ${params.value[1]}, ${params.value[2]})" 
                        style="padding: 4px 10px; background: ${
  isDarkTheme
    ? 'rgba(66, 153, 225, 0.2)'
    : 'rgba(66, 153, 225, 0.1)'
}; color: ${
  isDarkTheme ? '#63B3ED' : '#3182CE'
}; border-radius: 6px; cursor: pointer; font-size: 12px; font-weight: 500;">
                        View Details
                      </div>
                    </div>
                    <div style="margin-bottom: 8px;">
                      <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                        <span style="color: ${
  isDarkTheme ? '#94a3b8' : '#64748b'
}; font-size: 12px;">Experience:</span>
                        <span style="font-weight: 600;">${fallbackExperience} years</span>
                      </div>
                      <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                        <span style="color: ${
  isDarkTheme ? '#94a3b8' : '#64748b'
}; font-size: 12px;">${
  useSkillPercentage ? 'Skill Score' : 'Skills'
}:</span>
                        <span style="font-weight: 600;">${
  useSkillPercentage
    ? fallbackSkill + '%'
    : fallbackSkill
}</span>
                      </div>
                      <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                        <span style="color: ${
  isDarkTheme ? '#94a3b8' : '#64748b'
}; font-size: 12px;">Behavioral Attributes:</span>
                        <span style="font-weight: 600;">${fallbackBehavior}</span>
                      </div>
                      <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                        <span style="color: ${
  isDarkTheme ? '#94a3b8' : '#64748b'
}; font-size: 12px;">Keywords:</span>
                        <span style="font-weight: 600;">${fallbackKeyword}</span>
                      </div>
                    </div>
                  </div>
                `;
              }
              return '';
            }

            // Extract values from params for display
            const experience = params.value[1];
            const skillCount = params.value[0];
            const skillPercentage = params.value[0]; // Same as skillCount when using percentage
            const behaviorCount =
              params.value[2] !== undefined ? params.value[2] : 0;
            const keywordCount =
              params.value[3] !== undefined ? params.value[3] : 0;
            const tooltipStyles = `
              <style>
                .tooltip-container { 
                  min-width: 210px;
                  max-width: 260px;
                  font-family: 'Segoe UI', system-ui, -apple-system, sans-serif;
                }
                .tooltip-profiles { 
                  max-height: 120px;
                  overflow-y: auto; 
                  border-bottom: 1px solid ${
  isDarkTheme ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'
};
                  scrollbar-width: thin;
                  scrollbar-color: ${
  isDarkTheme
    ? 'rgba(255,255,255,0.2) rgba(0,0,0,0)'
    : 'rgba(0,0,0,0.2) rgba(0,0,0,0)'
};
                }
                .tooltip-header { 
                  display: flex; 
                  align-items: center; 
                  margin-bottom: 4px;
                  padding: 8px 10px 6px;
                  border-bottom: 1px solid ${
  isDarkTheme ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'
};
                }
                .tooltip-count { 
                  background: ${
  isDarkTheme
    ? 'rgba(99, 102, 241, 0.2)'
    : 'rgba(99, 102, 241, 0.1)'
};
                  padding: 2px 8px;
                  border-radius: 10px;
                  margin-right: 6px;
                  font-weight: 500;
                  font-size: 11px;
                  color: ${isDarkTheme ? '#818cf8' : '#6366f1'};
                }
                .view-candidates-btn {
                  padding: 4px 8px;
                  background: ${
  isDarkTheme
    ? 'rgba(66, 153, 225, 0.2)'
    : 'rgba(66, 153, 225, 0.1)'
};
                  color: ${isDarkTheme ? '#63B3ED' : '#3182CE'};
                  border: none;
                  border-radius: 6px;
                  cursor: pointer;
                  font-size: 11px;
                  font-weight: 500;
                  transition: all 0.2s;
                  display: inline-block;
                }
                .view-candidates-btn:hover {
                  background: ${
  isDarkTheme
    ? 'rgba(66, 153, 225, 0.3)'
    : 'rgba(66, 153, 225, 0.2)'
};
                  transform: translateY(-1px);
                }
                .tooltip-candidate { 
                  padding: 7px 10px;
                  transition: background 0.2s;
                  border-bottom: 1px solid ${
  isDarkTheme ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'
};
                }
                .tooltip-candidate:hover { 
                  background: ${
  isDarkTheme ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)'
};
                }
                .tooltip-name { 
                  font-weight: 600;
                  margin-bottom: 2px;
                  font-size: 13px;
                  color: ${isDarkTheme ? '#e2e8f0' : '#1e293b'};
                }
                .tooltip-role { 
                  font-size: 10px;
                  color: ${isDarkTheme ? '#94a3b8' : '#64748b'};
                }
                .tooltip-stats { 
                  padding: 8px 10px;
                  background: ${
  isDarkTheme ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.02)'
};
                  border-radius: 8px;
                  margin: 6px;
                }
                .tooltip-stat { 
                  display: flex;
                  justify-content: space-between;
                  align-items: center;
                  padding: 3px 0;
                }
                .tooltip-stat-label { 
                  color: ${isDarkTheme ? '#94a3b8' : '#64748b'};
                  font-size: 11px;
                }
                .tooltip-stat-value { 
                  font-weight: 600;
                  font-size: 11px;
                  color: ${isDarkTheme ? '#e2e8f0' : '#1e293b'};
                }
                .match-score { 
                  display: inline-flex;
                  align-items: center;
                  padding: 2px 7px;
                  border-radius: 6px;
                  margin-left: 6px;
                  font-size: 10px;
                  font-weight: 500;
                }
              </style>
            `;

            // Calculate match score (example: based on skills and behavior matches)
            const getMatchScore = (candidate: any) => {
              // Handle cases where candidate may have undefined or null arrays
              const candidateSkills = candidate.matchedSkills || [];
              const candidateBehavior = candidate.matchedBehavior || [];
              const totalSkills = additionalSkills?.skills?.length || 1;
              const totalBehavior =
                behavioralAttributes?.behavioralAttributes?.length || 1;

              const skillsMatch = candidateSkills.length / totalSkills;
              const behaviorMatch = candidate.value[2] / totalBehavior;

              return Math.round((skillsMatch + behaviorMatch) * 50); // Scale to 0-100
            };

            const getScoreColor = (score: number) => {
              // Color gradient from blue (low) to red (high)
              if (score >= 75) return isDarkTheme ? '#F56565' : '#E53E3E'; // Red
              if (score >= 50) return isDarkTheme ? '#ED8936' : '#DD6B20'; // Orange
              if (score >= 25) return isDarkTheme ? '#4299E1' : '#3182CE'; // Light Blue
              return isDarkTheme ? '#2B6CB0' : '#2C5282'; // Dark Blue
            };

            return `
              ${tooltipStyles}
              <div class="tooltip-container">
                ${
  candidates.length > 1
    ? `
                  <div class="tooltip-header">
                    <div style="display: flex; justify-content: space-between; align-items: center; width: 100%;">
                      <div style="display: flex; align-items: center;">
                        <span class="tooltip-count">${candidates.length}</span>
                        <span>candidates at this position</span>
                      </div>
                      <div onclick="window.openScatterModal(${params.value[0]}, ${params.value[1]}, ${params.value[2]})" 
                        class="view-candidates-btn">
                        View Candidates
                      </div>
                    </div>
                  </div>
                `
    : ''
}

                <div class="tooltip-profiles">
                  ${candidates
    .map((candidate) => {
      const matchScore = getMatchScore(candidate);
      const scoreColor = getScoreColor(matchScore);
      return `
                      <div class="tooltip-candidate">
                        <div class="tooltip-name">
                          ${candidate.name}
                          <span class="match-score" style="background: ${scoreColor}20; color: ${scoreColor}">
                            ${matchScore}%
                          </span>
                        </div>
                        ${
  candidate.locationCity
    ? `
                          <div class="tooltip-role">${candidate.locationCity}${
  candidate.locationCountry
    ? `, ${candidate.locationCountry}`
    : ''
}</div>
                        `
    : ''
}
                      </div>
                    `;
    })
    .join('')}
                </div>

                <div class="tooltip-stats">

                  <div class="tooltip-stat">
                    <span class="tooltip-stat-label">Experience</span>
                    <span class="tooltip-stat-value">${experience} years</span>
                  </div>
                    <div class="tooltip-stat">
                    <span class="tooltip-stat-label">${
  useSkillPercentage ? 'Skill Score' : 'Skills'
}</span>
                    <span class="tooltip-stat-value">${
  useSkillPercentage
    ? parseFloat(skillPercentage).toFixed(2) + '%'
    : skillCount
}</span>
                  </div>
                  <div class="tooltip-stat">
                    <span class="tooltip-stat-label">Behavioural Attributes</span>
                    <span class="tooltip-stat-value">${behaviorCount}</span>
                  </div>
                  <div class="tooltip-stat">
                    <span class="tooltip-stat-label">Keywords</span>
                    <span class="tooltip-stat-value">${keywordCount}</span>
                  </div>
                </div>
              </div>
            `;
          },
        },
        visualMap: {
          max: maxLegion,
          min: 0,
          text: ['Keywords'],
          dimension: 3, // Map to the 4th dimension (keywords)
          inRange: {
            color: colorList,
          },
          itemWidth: 14, // Increased width for better visibility
          itemHeight: 140, // Significantly increased height to show gradient properly
          textStyle: {
            color: isDarkTheme ? '#ffffff' : '#808080',
          },
          left: '5%', // Position to left bottom corner
          bottom: '10%', // Position from bottom
          orient: 'vertical', // Make legend vertical
        },
        xAxis3D: {
          name: useSkillPercentage
            ? 'Weighted Skill Score (%)'
            : 'Number of Skills',
          nameTextStyle: {
            color: isDarkTheme
              ? 'rgba(255, 255, 255, 0.85)'
              : 'rgba(0, 0, 0, 0.85)',
            fontSize: 14,
            fontWeight: 500,
            padding: [15, 0, 0, 0],
          },
          min: 0,
          max: useSkillPercentage
            ? 100
            : Math.min(
              100,
              Math.ceil(Math.max(...data.map((item) => item.value[0]))),
            ),
          type: 'value',
          axisLabel: {
            formatter: function (value: number) {
              return useSkillPercentage ? value + '%' : value;
            },
            margin: 8,
            fontSize: 12,
          },
          splitNumber: 5,
          splitArea: {
            show: true,
            areaStyle: {
              color: isDarkTheme
                ? 'rgba(255,255,255,0.02)'
                : 'rgba(0,0,0,0.02)',
            },
          },
        },
        yAxis3D: {
          name: 'Years of Experience',
          nameTextStyle: {
            color: isDarkTheme
              ? 'rgba(255, 255, 255, 0.85)'
              : 'rgba(0, 0, 0, 0.85)',
            fontSize: 14,
            fontWeight: 500,
          },
          min: Math.floor(Math.min(...data.map((item) => item.value[1]))),
          max: Math.ceil(Math.max(...data.map((item) => item.value[1])) * 1.1),
          splitNumber: 5,
          axisLabel: {
            formatter: function (value: number) {
              return value + ' years';
            },
            margin: 8,
            fontSize: 12,
          },
        },
        zAxis3D: {
          type: 'value',
          name: 'Behavior Score',
          nameTextStyle: {
            color: isDarkTheme
              ? 'rgba(255, 255, 255, 0.85)'
              : 'rgba(0, 0, 0, 0.85)',
            fontSize: 14,
            fontWeight: 500,
          },
          min: 0, // Set explicit minimum
          max: behavioralAttributes?.behavioralAttributes?.length || 10, // Set max to number of behavioral attributes, fallback to 10
          splitNumber: 5,
          axisLabel: {
            formatter: function (value: number) {
              return value.toString();
            },
            margin: 8,
            fontSize: 12,
          },
        },
        grid3D: {
          boxWidth: 120,
          boxHeight: 100,
          boxDepth: 100,
          top: '0', // Reduced from 8% to move chart up
          bottom: '10%', // Added bottom margin for legend space
          containLabel: true,
          tooltip: {
            show: true,
            trigger: 'item',
          },
          axisLabel: {
            show: true,
            textStyle: {
              fontSize: 12,
            },
            margin: 8,
            formatter: (value: number) =>
              useSkillPercentage ? `${value.toFixed(0)}%` : value.toFixed(0),
          },
          axisTick: {
            show: true,
            length: 2,
          },
          splitLine: {
            show: true,
            lineStyle: {
              color: isDarkTheme ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
              width: 1,
            },
          },
          viewControl: {
            projection: projection,
            distance: 200, // Increased for better overall view
            alpha: 20, // Adjusted for clearer axis labels
            beta: 40, // Better initial rotation
            animation: true,
            animationDurationUpdate: 500,
            animationEasingUpdate: 'cubicInOut',
            rotateSensitivity: 1,
            zoomSensitivity: 1.2,
            panSensitivity: 1,
            autoRotate: false,
            damping: 0.8,
            minDistance: 120,
            maxDistance: 400,
            orthographicSize: 100,
            orthographic: projection === 'orthographic',
          },
          light: {
            main: {
              intensity: 1.2,
              alpha: 20,
              beta: 120,
            },
            ambient: {
              intensity: 0.3,
            },
          },
          environment: isDarkTheme ? '#0a1929' : '#fff',
          axisPointer: {
            show: true,
            lineStyle: {
              color: isDarkTheme ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
              width: 1,
            },
          },
          axisLine: {
            lineStyle: {
              color: isDarkTheme ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
            },
          },
          mouseIndicator: {
            show: true,
          },
        },
        series: [
          {
            type: scatter ? 'scatter3D' : 'bar3D',
            tooltip: {
              show: true,
              enterable: true,
              trigger: 'item',
            },
            data: newData.map((item) => {
              // Calculate skill score based on whether weighted score is enabled
              let skillScore;
              if (useSkillPercentage) {
                // If percentage mode is enabled, ensure value is between 0-100
                skillScore = Number(item.value[0]);
                if (!isNaN(skillScore)) {
                  // Clamp value between 0 and 100
                  skillScore = Math.min(100, Math.max(0, skillScore));
                  skillScore = Number(skillScore.toFixed(2));
                } else {
                  skillScore = 0;
                }
              } else {
                // In count mode, use the raw skill count
                skillScore = Math.round(item.value[0]);
              }

              // Calculate match score
              const skillsMatch = useSkillPercentage
                ? skillScore / 100 // If in percentage mode, use the percentage directly
                : (Array.isArray(item.matchedSkills)
                  ? item.matchedSkills.length
                  : 0) / (additionalSkills?.skills?.length || 1);

              const behaviorMatch =
                (Array.isArray(item.matchedBehavior)
                  ? item.matchedBehavior.length
                  : 0) /
                (behavioralAttributes?.behavioralAttributes?.length || 1);

              const matchScore = (skillsMatch + behaviorMatch) * 50; // Scale to 0-100

              // Get keyword count for color mapping - ensure we handle zero values properly
              // When keywords is 0 but useSkillPercentage is true, we need to explicitly set 0
              // to make sure filtering works correctly and tooltips display
              const keywordCount = Array.isArray(item.keywords)
                ? item.keywords.length
                : item.value[3] !== undefined
                  ? item.value[3]
                  : 0;

              return {
                ...item,
                value: [skillScore, item.value[1], item.value[2], keywordCount], // Use weighted skillScore when enabled
                itemStyle: {
                  opacity: 1,
                  borderWidth: 1,
                  shadowColor: 'rgba(0, 0, 0, 0.2)',
                  borderColor: isDarkTheme
                    ? 'rgba(255,255,255,0.2)'
                    : 'rgba(0,0,0,0.1)',
                },
                symbolSize: scatter
                  ? Math.max(16, matchScore * 0.3 + 16)
                  : undefined, // Size based on match score
              };
            }),
            barSize: 8,
            stack: 'stack',
            shading: 'realistic',
            itemStyle: {
              opacity: 0.8,
            },
            visualMap: {
              show: false, // Hide the duplicate visualMap in series
              dimension: 3, // Map to the 4th dimension (keywords)
              min: 0,
              max: maxLegion,
              inRange: {
                color: colorList,
              },
            },
            emphasis: {
              itemStyle: {
                opacity: 1,
              },
              label: {
                show: true,
                formatter: function (params: any) {
                  return params.data.name;
                },

                backgroundColor: isDarkTheme
                  ? 'rgba(0,0,0,0.75)'
                  : 'rgba(255,255,255,0.75)',
                padding: [4, 8],
                borderRadius: 4,
              },
            },
          },
        ],
      };
      myChart.setOption(option);
      resizeHandler = () => myChart.resize();

      if (typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(resizeHandler);
        resizeObserver.observe(chartRef.current);
      }

      window.addEventListener('resize', resizeHandler);

      myChart.on('click', function (param) {
        // Filter candidates based on the clicked point
        const filteredData = data.filter((item) => {
          const itemValue = item.value as number[];
          const paramValue = param.value as number[];

          // Get the skill value that should be used for comparison
          let itemSkillValue = itemValue[0];
          if (useSkillPercentage) {
            // If in percentage mode, get the skillPercentage from the original data
            itemSkillValue = Number(item.value[0] || 0);
          }

          // Use a small tolerance for floating point comparison
          const tolerance = 0.001;
          return (
            Math.abs(itemSkillValue - paramValue[0]) < tolerance &&
            Math.abs(itemValue[1] - paramValue[1]) < tolerance &&
            Math.abs(itemValue[2] - paramValue[2]) < tolerance
          );
        });

        // If no exact match found (which can happen with scatter plots),
        // try to find candidates with matching userId from the clicked data
        if (
          filteredData.length === 0 &&
          param.data &&
          typeof param.data === 'object'
        ) {
          const dataObj = param.data as any;
          if (dataObj.userId) {
            const candidateById = data.filter(
              (item) => item.userId === dataObj.userId,
            );
            if (candidateById.length > 0) {
              setSegmentedCandidates(candidateById);
              setIsModalOpen(true);
              return;
            }
          }
        }

        if (filteredData.length === 1 && onCandidateClick) {
          onCandidateClick(filteredData[0]);
          return;
        }

        setSegmentedCandidates(filteredData);
        setIsModalOpen(true);
      });
    }

    return () => {
      resizeObserver?.disconnect();
      if (resizeHandler) {
        window.removeEventListener('resize', resizeHandler);
      }
      if (scatterModalHandler) {
        window.removeEventListener('openScatterModal', scatterModalHandler);
      }
      if (chartRef.current) {
        echarts.dispose(chartRef.current);
      }
      // Clean up global handler
      delete (window as any).openScatterModal;
    };
  }, [
    data,
    scatter,
    colorList,
    isDarkTheme,
    projection,
    useSkillPercentage,
    maxLegion,
  ]);

  const loadModalContent = () => {
    return (
      <ScatterModal
          segmentedCandidates={segmentedCandidates}
          selectedCandidates={selectedCandidates}
          setSelectedCandidates={setSelectedCandidates}
          setActiveTab={setActiveTab}
          setTabTitles={setTabTitles}
          tabTitles={tabTitles}
          useSkillPercentage={useSkillPercentage}
          onCandidateClick={(candidate) => {
            if (onCandidateClick) {
              onCandidateClick(candidate);
            }
            setIsModalOpen(false);
          }}
          filteredSkills={additionalSkills?.skills.map((s) => s.skill)}
      />
    );
  };
  const themeClass = isDarkTheme
    ? 'bg-[#0A1929] text-white'
    : 'bg-white text-gray-900';

  const cardClass = isDarkTheme
    ? 'bg-[#132F4C] border-[#1E4976]'
    : 'bg-white border-gray-200';

  return (
    <div className={`h-full w-full transition-all duration-500 ${themeClass}`}>
      <div className='flex w-full flex-col gap-3 xl:flex-row'>
        { /* Main Chart Area */ }
        <div className='min-w-0 flex-1 px-2 py-2 sm:px-3'>
          <div className='h-full w-full'>
            <div className='mb-2 flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between'>
              <h1 className='text-lg font-semibold sm:text-xl'>
                3D Candidate Analysis
              </h1>
              <p
                  className={`text-xs sm:text-sm ${
                    isDarkTheme ? 'text-gray-400' : 'text-gray-600'
                  }`}
              >
                Interactive visualization •{ ' ' }
                { data ? `${data.length} candidates` : 'Loading...' }
              </p>
            </div>

            <div
                className={`relative min-h-[360px] rounded-xl border ${cardClass} shadow-sm overflow-hidden`}
            >
              <div className='absolute left-3 top-3 z-10'>
                <div
                    className={`px-2.5 py-1 rounded-lg ${
                      isDarkTheme
                        ? 'bg-gray-800 bg-opacity-90'
                        : 'bg-white bg-opacity-90'
                    } backdrop-blur-sm border ${
                      isDarkTheme ? 'border-gray-600' : 'border-gray-300'
                    }`}
                >
                  <p className='text-xs font-medium sm:text-sm'>
                    { data ? `${data.length} candidates` : 'Loading...' }
                  </p>
                </div>
              </div>

              <div className='absolute right-3 top-3 z-10 hidden sm:block'>
                <div
                    className={`px-2.5 py-1 rounded-lg ${
                      isDarkTheme
                        ? 'bg-gray-800 bg-opacity-90'
                        : 'bg-white bg-opacity-90'
                    } backdrop-blur-sm border ${
                      isDarkTheme ? 'border-gray-600' : 'border-gray-300'
                    }`}
                >
                  <p className='text-xs opacity-75'>
                    Drag to rotate • Click points for details
                  </p>
                </div>
              </div>

              <div
                  ref={chartRef}
                  style={{
                    height: 'clamp(360px, calc(100dvh - 260px), 640px)',
                    width: '100%',
                  }}
              />
            </div>
          </div>
        </div>

        { /* Stats Panel */ }
        <aside className='w-full border-t border-opacity-20 px-2 py-2 sm:px-3 xl:w-72 xl:shrink-0 xl:border-l xl:border-t-0'>
          <div className='grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-1'>
            <div className='flex items-center justify-between'>
              <h2 className='text-lg font-semibold'>Analytics</h2>
              <button
                  onClick={() => setIsDarkTheme(!isDarkTheme)}
                  className={`p-2 rounded-lg transition-colors ${
                    isDarkTheme
                      ? 'hover:bg-[#1E4976] text-gray-200'
                      : 'hover:bg-gray-100 text-gray-700'
                  }`}
              >
                { isDarkTheme ? (
                  <SunIcons className='w-5 h-5' />
                ) : (
                  <MoonIcons className='w-5 h-5' />
                ) }
              </button>
            </div>

            <div className={`rounded-xl border p-3 ${cardClass}`}>
              <div className='flex items-center space-x-3'>
                <div className='p-1.5 rounded-lg bg-blue-500 bg-opacity-20'>
                  <UsersIcons className='w-4 h-4 text-blue-500' />
                </div>
                <div>
                  <p className='text-xs opacity-75'>Total Candidates</p>
                  <p className='text-xl font-bold'>{ stats.totalCandidates }</p>
                </div>
              </div>
            </div>

            <div className={`rounded-xl border p-3 ${cardClass}`}>
              <div className='flex items-center space-x-3'>
                <div className='p-1.5 rounded-lg bg-green-500 bg-opacity-20'>
                  <TrendingUpIcons className='w-4 h-4 text-green-500' />
                </div>
                <div>
                  <p className='text-xs opacity-75'>Avg Skill Score</p>
                  <p className='text-xl font-bold'>
                    { stats.avgSkillScore }
                    { useSkillPercentage ? '%' : '' }
                  </p>
                </div>
              </div>
            </div>

            <div className={`rounded-xl border p-3 ${cardClass}`}>
              <div className='flex items-center space-x-3'>
                <div className='p-1.5 rounded-lg bg-purple-500 bg-opacity-20'>
                  <ChartIcons className='w-4 h-4 text-purple-500' />
                </div>
                <div>
                  <p className='text-xs opacity-75'>Avg Experience</p>
                  <p className='text-xl font-bold'>
                    { stats.avgExperience } yrs
                  </p>
                </div>
              </div>
            </div>

            <div className={`rounded-xl border p-3 ${cardClass}`}>
              <div className='flex items-center space-x-3'>
                <div className='p-1.5 rounded-lg bg-orange-500 bg-opacity-20'>
                  <TargetIcons className='w-4 h-4 text-orange-500' />
                </div>
                <div>
                  <p className='text-xs opacity-75'>Avg Behavior Score</p>
                  <p className='text-xl font-bold'>{ stats.avgBehaviorScore }</p>
                </div>
              </div>
            </div>

            { /* Color Legend */ }
            <div className='space-y-2 rounded-xl border border-opacity-20 p-3 sm:col-span-2 xl:col-span-1'>
              <h3 className='text-sm font-medium'>Keywords Legend</h3>
              <div className='relative'>
                { /* Gradient Bar */ }
                <div
                    className='h-4 rounded-md relative overflow-hidden'
                    style={{
                      background: `linear-gradient(to right, ${colorList[0]}, ${
                        colorList[Math.floor(colorList.length / 2)]
                      }, ${colorList[colorList.length - 1]})`,
                    }}
                />

                { /* Tick Marks and Labels */ }
                <div className='flex justify-between mt-1 px-0.5'>
                  <div className='flex flex-col items-start'>
                    <div className='w-0.5 h-1.5 bg-current mb-1' />
                    <span className='text-[10px] font-medium'>0</span>
                  </div>
                  <div className='flex flex-col items-center'>
                    <div className='w-0.5 h-1.5 bg-current mb-1' />
                    <span className='text-[10px] font-medium'>
                      { Math.round(maxLegion / 2) }
                    </span>
                  </div>
                  <div className='flex flex-col items-end'>
                    <div className='w-0.5 h-1.5 bg-current mb-1' />
                    <span className='text-[10px] font-medium'>{ maxLegion }</span>
                  </div>
                </div>

                { /* Description */ }
                <p className='text-[10px] mt-2 text-center opacity-75'>
                  Number of keywords in candidate profile
                </p>
              </div>
            </div>
            { /* Skills from Job Opening */ }
            <div className={`rounded-xl border p-3 sm:col-span-2 xl:col-span-1 ${cardClass}`}>
              <div className='flex flex-col space-y-3'>
                <div className='flex items-center gap-2'>
                  <div className='p-1.5 rounded-lg bg-blue-500 bg-opacity-20'>
                    <ChartIcons className='w-4 h-4 text-blue-500' />
                  </div>
                  <p className='text-sm font-medium'>Core Competencies</p>
                </div>

                { /* Job Titles */ }
                { jobTitles?.jobTitles && jobTitles.jobTitles.length > 0 && (
                  <div className='space-y-2'>
                    <p className='text-xs font-medium opacity-75'>Job Titles</p>
                    <div className='flex flex-wrap gap-1'>
                      { jobTitles.jobTitles.map((title: string, i: number) => (
                        <span
                            key={i}
                            className={`px-2 py-0.5 text-[10px] rounded-full ${
                              isDarkTheme
                                ? 'bg-green-500/20 text-green-200'
                                : 'bg-green-100 text-green-700'
                            }`}
                        >
                          { title }
                        </span>
                      )) }
                    </div>
                  </div>
                ) }
                { /* Additional Skills */ }
                { additionalSkills?.skills &&
                  additionalSkills.skills.length > 0 && (
                  <div className='space-y-2'>
                    <p className='text-xs font-medium opacity-75'>Skills</p>
                    <div className='flex flex-wrap gap-1'>
                      { additionalSkills.skills.map((skill, i: number) => (
                        <span
                            key={i}
                            className={`px-2 py-0.5 text-[10px] rounded-full ${
                              isDarkTheme
                                ? 'bg-blue-500/20 text-blue-200'
                                : 'bg-blue-100 text-blue-700'
                            }`}
                        >
                          { skill.skill }
                        </span>
                      )) }
                    </div>
                  </div>
                ) }
                { /* Keywords */ }
                { /* Behavioral Attributes */ }
                { behavioralAttributes?.behavioralAttributes &&
                  behavioralAttributes.behavioralAttributes.length > 0 && (
                  <div className='space-y-2'>
                    <p className='text-xs font-medium opacity-75'>
                      Behavioral Attributes
                    </p>
                    <div className='flex flex-wrap gap-1'>
                      { behavioralAttributes.behavioralAttributes.map(
                        (attr: string, i: number) => (
                          <span
                              key={i}
                              className={`px-2 py-0.5 text-[10px] rounded-full ${
                                isDarkTheme
                                  ? 'bg-orange-500/20 text-orange-200'
                                  : 'bg-orange-100 text-orange-700'
                              }`}
                          >
                            { attr }
                          </span>
                        ),
                      ) }
                    </div>
                  </div>
                ) }
                { keywords?.keywords && keywords.keywords.length > 0 && (
                  <div className='space-y-2'>
                    <p className='text-xs font-medium opacity-75'>Keywords</p>
                    <div className='flex flex-wrap gap-1'>
                      { keywords.keywords.map((kw, i: number) => (
                        <span
                            key={i}
                            className={`px-2 py-0.5 text-[10px] rounded-full ${
                              isDarkTheme
                                ? 'bg-purple-500/20 text-purple-200'
                                : 'bg-purple-100 text-purple-700'
                            }`}
                        >
                          { kw.keyword }
                        </span>
                      )) }
                    </div>
                  </div>
                ) }

                { /* Total Count */ }
                <div className='pt-2 border-t border-opacity-20'>
                  <div className='flex justify-between items-center'>
                    <span className='text-xs opacity-75'>
                      Total Core Competencies
                    </span>
                    <span className='text-sm font-medium'>
                      { (additionalSkills?.skills?.length || 0) +
                        (jobTitles?.jobTitles?.length || 0) +
                        (keywords?.keywords?.length || 0) +
                        (behavioralAttributes?.behavioralAttributes?.length ||
                          0) }
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </aside>
      </div>

      <Modal
          visible={isModalOpen}
          toggle={() => setIsModalOpen(false)}
          modalContent={loadModalContent()}
          customClass={styles.modalCustomClass}
          headerClass={styles.headerBackground}
          headerText={`Number of Candidates: ${segmentedCandidates.length}`}
      />
    </div>
  );
};
